import {
  IsBoolean,
  IsEnum,
  IsInt,
  IsNotEmpty,
  IsString,
  ValidateIf,
  ValidateNested,
} from '@pristine-ts/class-validator';
import { translationBaseKey } from '../../../decorators/translation-base-key.decorator';
import { FieldTypeEnum } from '../../../enums/field-type.enum';
import { FormControlTypeEnum, advancedFormGroup, advancedFormControl, advancedFormArray } from '@magieno/common';
import { CivilStatusEnum } from '../../../enums/civil-status.enum';
import { Quebec2022TaxQuestionnaireBaseOptions } from './quebec-2022-tax-questionnaire-base.options';
import { ClientQuestionnaireKeynameEnum } from '../../../enums/client-questionnaire/client-questionnaire-keyname.enum';
import { Quebec2022TaxQuestionnaireChildOptions } from './quebec-2022-tax-questionnaire-child.options';
import { ErrorMessageBuilder } from '../../../builders/error-message.builder';
import { Type } from 'class-transformer';

@translationBaseKey('quebec-2022-tax-questionnaire-options')
export class MainSpouseQuebec2022TaxQuestionnaireOptions extends Quebec2022TaxQuestionnaireBaseOptions {

  @advancedFormControl({
    type: FormControlTypeEnum.Hidden,
  })
  keyname: ClientQuestionnaireKeynameEnum = ClientQuestionnaireKeynameEnum.QuebecTaxForm2022;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous des enfants à charge durant l\'année d\'imposition?',
        en: '',
      },
      tooltip: {
        fr: 'Tooltip',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  doYouHaveDependentChildren: boolean;

  @Type(() => Quebec2022TaxQuestionnaireChildOptions)
  @advancedFormArray({
    emptyNumberOfChildrenFormToCreate: 0,
    elementClassType: Quebec2022TaxQuestionnaireChildOptions,
  })
  @ValidateNested()
  children: Quebec2022TaxQuestionnaireChildOptions[] = [];

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Est-ce que la déclaration du conjoint sera produite par notre cabinet?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.civilStatus === CivilStatusEnum.Married || o.civilStatus === CivilStatusEnum.LivingCommonLaw)
  isSpouseTaxPrepared: boolean;

  @Type(() => Quebec2022TaxQuestionnaireBaseOptions)
  @advancedFormGroup({
    classType: Quebec2022TaxQuestionnaireBaseOptions,
  })
  @ValidateNested()
  @ValidateIf(o => o.isSpouseTaxPrepared === true)
  spouseTaxForm: Quebec2022TaxQuestionnaireBaseOptions;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Quel est le revenu net du conjoint selon la ligne 23600 (fédéral)?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.isSpouseTaxPrepared === false)
  spouseFederalNetIncome: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Quel est le revenu net du conjoint selon la ligne 27500 (Québec)?',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.isSpouseTaxPrepared === false)
  spouseQuebecNetIncome: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Quel est votre état civil?',
        en: '?',
      },
      tooltip: {
        fr: 'Si vous étiez marié ou en union libre le 31 décembre de l\'année fiscale en question, vous devrez fournir des renseignements sur votre conjoint. Ensuite, le questionnaire sera ajusté en conséquence pour inclure ces informations.',
      },
    },
    items: [
      {
        label: {
          fr: 'Célibataire',
          en: '',
        },
        id: CivilStatusEnum.Single,
      },
      {
        label: {
          fr: 'Marié(e)',
          en: '',
        },
        id: CivilStatusEnum.Married,
      },
      {
        label: {
          fr: 'Conjoint(e)',
          en: '',
        },
        id: CivilStatusEnum.LivingCommonLaw,
      },
      {
        label: {
          fr: 'Divorcé(e)',
          en: '',
        },
        id: CivilStatusEnum.Divorced,
      },
      {
        label: {
          fr: 'Séparé(e)',
          en: '',
        },
        id: CivilStatusEnum.Separated,
      },
      {
        label: {
          fr: 'Veuf (veuve)',
          en: '',
        },
        id: CivilStatusEnum.Widowed,
      },
    ],
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Select,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsEnum(CivilStatusEnum, { buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  civilStatus: CivilStatusEnum;
}


